'use client';

import { AnimatePresence } from 'framer-motion';
import {
  NavigationByIdsFragment,
  NavigationFragment,
  NavigationLinkFragment,
  NavigationSublinkFragment,
  PageFragment,
} from '@business/gql/graphql';
import { Dialog } from './components/Dialog';
import DialogClose from './components/Dialog/DialogClose';
import MobileMenuHeader from './components/MobileMenuHeader';
import { useCallback, useMemo, useState } from 'react';
import MobileMenuTop from './components/MobileMenuTop';
import MobileMenuLinks from './components/MobileMenuLinks';
import {
  getNavigationCollectionByNameAction,
  getNavigationLinksIdByNavigationName,
  getNavigationSublinksById,
} from '@app/actions/navigation/actions';
import { QueryKeys } from '@utils/queries/queryKeys';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { getStoresCollectionAction } from '@app/actions/store/actions';
import MobileMenuBottom from './components/MobileMenuBottom';
import { cn } from '@ngg/storefront-utils';
import { Maybe } from 'graphql/jsutils/Maybe';
import useDictionary from '@utils/hooks/useDictionary';
import config from '@utils/config';

const CUSTOMER_SERVICE_NORWAY_ENTRY_ID = '1qeflMTodXIEXlTHGKINUh';
const CUSTOMER_SERVICE_SWEDEN_ENTRY_ID = '5C995hTPw5rWIEvLJoYV95';
const PICK_UP_WAREHOUSE_ENTRY_ID = '4DxnOv0IuHtsgXK3uHLhUH';

const getCustomerServiceEntryId = (locale: string) => {
  if (locale === 'nb-no') return CUSTOMER_SERVICE_NORWAY_ENTRY_ID;

  return CUSTOMER_SERVICE_SWEDEN_ENTRY_ID;
};

type Props = {
  remainingNavigationElements?: Array<NavigationFragment | null>;
  newsElement: NavigationFragment | null;
  campaignElement: NavigationFragment | null;
  open: boolean;
  setOpen: (value: boolean) => void;
  activeNavigationElement: string | null;
};

export default function MobileMenu(props: Props) {
  const dictionary = useDictionary({
    keys: ['close', 'back', 'showAll', 'menu', 'searchTerm'],
  });
  const locale = config.locale;

  const { data } = useQuery({
    queryKey: [
      QueryKeys.navigationLinks,
      locale,
      props.activeNavigationElement,
    ],
    queryFn: () => {
      return getNavigationCollectionByNameAction({
        locale: locale,
        name: props.activeNavigationElement ?? '',
      });
    },
  });

  return (
    <MobileMenuUI
      {...props}
      locale={locale}
      menuDictionary={dictionary}
      additionalNavigationLinks={
        data?.additionalNavigationLinksCollection?.items
      }
      mainNavigationLinks={data?.sublinksCollection?.items}
    />
  );
}

type PropsUI = Omit<Props, 'activeNavigationElement'> & {
  additionalNavigationLinks?: Array<NavigationLinkFragment | null>;
  mainNavigationLinks?: Array<NavigationLinkFragment | null>;
  locale: string;
  menuDictionary: Record<string, string>;
};

function MobileMenuUI({
  remainingNavigationElements,
  additionalNavigationLinks,
  mainNavigationLinks,
  open,
  setOpen,
  newsElement,
  campaignElement,
  locale,
  menuDictionary,
}: PropsUI) {
  const queryClient = useQueryClient();

  const [menuLevel, setMenuLevel] = useState(0);
  const [navigationDirection, setNavigationDirection] = useState<
    'left' | 'right'
  >('right');

  const [subMenu1Items, setSubMenu1Items] = useState<
    NavigationSublinkFragment | undefined | null
  >(undefined);

  const [subMenu2Items, setSubMenu2Items] = useState<
    NavigationSublinkFragment | undefined | null
  >(undefined);

  const [navigationLinkCollection, setNavigationLinkCollection] = useState<
    Maybe<NavigationByIdsFragment>[] | undefined
  >(undefined);

  const [currentNavigationLevel, setCurrentNavigationLevel] = useState<
    'campaign' | undefined
  >();

  const onClose = () => {
    setNavigationDirection('right');
    setOpen(false);
    setMenuLevel(0);
    setNavigationLinkCollection(undefined);
    setCurrentNavigationLevel(undefined);
  };

  const { data: storeData } = useQuery({
    queryKey: [QueryKeys.stores, locale],
    queryFn: () => {
      return getStoresCollectionAction({ locale });
    },
  });

  const onlineStores = useMemo(() => {
    return storeData?.filter(
      (store) =>
        store?.type === 'Online' &&
        store?.sys?.id === getCustomerServiceEntryId(locale),
    );
  }, [locale, storeData]);

  const physicalStores = useMemo(() => {
    return storeData?.filter(
      (store) =>
        store?.type === 'Store' &&
        store?.address &&
        store?.title &&
        store?.sys?.id !== PICK_UP_WAREHOUSE_ENTRY_ID,
    );
  }, [storeData]);

  const onMobileMenuClick = useCallback(
    async (id: string) => {
      if (menuLevel === 0 || menuLevel === 1) {
        setNavigationDirection('right');

        const result = await queryClient.fetchQuery({
          queryKey: [QueryKeys.navigationLinks, locale, id],
          queryFn: () => {
            return getNavigationSublinksById({
              locale,
              id,
            });
          },
        });

        menuLevel === 0 ? setSubMenu1Items(result) : setSubMenu2Items(result);

        setMenuLevel(menuLevel + 1);
        setCurrentNavigationLevel(undefined);
      }
    },
    [locale, menuLevel, queryClient],
  );

  const onCampaignNavigationClick = useCallback(
    async (name: string) => {
      setNavigationDirection('right');

      const result = await queryClient.fetchQuery({
        queryKey: [QueryKeys.getNavigationLinksIdByName, locale, name],
        queryFn: () => {
          return getNavigationLinksIdByNavigationName(name);
        },
      });
      setNavigationLinkCollection(result);
      setCurrentNavigationLevel('campaign');
    },
    [locale, queryClient],
  );

  return (
    <AnimatePresence>
      {open && (
        <Dialog.Root
          static
          open={open}
          onClose={onClose}
          className="relative z-50 font-sans">
          <Dialog.Overlay />
          <Dialog.Content
            className="relative flex grow flex-col content-stretch bg-main text-sm"
            position="left"
            open={open}
            onClose={onClose}
            closeComponent={
              <DialogClose
                onClick={onClose}
                className="top-3 px-4 text-xs tracking-wide underline outline-none">
                {menuDictionary.close}
              </DialogClose>
            }>
            <Dialog.Header className="border-borderColor border-b py-3">
              <MobileMenuHeader
                menuDictionary={menuDictionary}
                show={menuLevel > 0 || Boolean(navigationLinkCollection)}
                onClick={() => {
                  setNavigationDirection('left');

                  if (navigationLinkCollection) {
                    setNavigationLinkCollection(undefined);
                    setCurrentNavigationLevel(undefined);
                    return;
                  }

                  setMenuLevel(menuLevel - 1);
                }}
              />
            </Dialog.Header>
            <MobileMenuTop
              onClose={onClose}
              menuDictionary={menuDictionary}
              menuLevel={menuLevel}
              currentNavigationLevel={currentNavigationLevel}
              {...(menuLevel === 1 && {
                topSlug: subMenu1Items?.internalLink?.slug,
                topTitle: subMenu1Items?.title,
              })}
              {...(menuLevel === 2 && {
                topSlug: subMenu2Items?.internalLink?.slug,
                topTitle: subMenu2Items?.title,
              })}
              {...(currentNavigationLevel === 'campaign' && {
                topSlug: campaignElement?.page?.slug,
                topTitle: campaignElement?.title,
              })}
            />
            <MobileMenuLinks
              navigationDirection={navigationDirection}
              newsElement={newsElement}
              remainingNavigation={remainingNavigationElements}
              campaignElement={campaignElement}
              navigationLinkCollection={navigationLinkCollection}
              sublinksNavigation={
                menuLevel === 1 ? subMenu1Items : subMenu2Items
              }
              mainNavigation={mainNavigationLinks}
              locale={locale}
              currentNavigationLevel={currentNavigationLevel}
              onClose={onClose}
              menuLevel={menuLevel}
              onClick={onMobileMenuClick}
              onCampaignNavigationClick={onCampaignNavigationClick}
            />
            <MobileMenuBottom
              className={cn([
                'h-full',
                menuLevel === 0 && 'border-t-1 border-borderColor bg-beige',
                menuLevel === 1 && subMenu1Items?.highlight && 'bg-beige',
                menuLevel === 2 && subMenu2Items?.highlight && 'bg-beige',
              ])}
              additionalNavigation={additionalNavigationLinks}
              onlineStores={onlineStores}
              physicalStores={physicalStores}
              locale={locale}
              menuLevel={menuLevel}
              currentNavigationLevel={currentNavigationLevel}
              onClick={onClose}
              navigationDirection={navigationDirection}
              splash={
                menuLevel > 0 && menuLevel === 1
                  ? subMenu1Items?.highlight
                  : subMenu2Items?.highlight
              }
            />
          </Dialog.Content>
        </Dialog.Root>
      )}
    </AnimatePresence>
  );
}
